'use client'

import { cn } from '@brew/ui/cn'
import { Fragment, type ReactNode, useEffect, useMemo } from 'react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useSetPageTitle } from '@/contexts/page-title-context'
import { PAGE_TITLE_CLASSES } from '@/lib/shared/layout-constants'

export type DetailPageHeaderProps = {
  /** Label for the parent collection, such as API or Automations. */
  parentLabel: string
  /** Route back to the parent collection. */
  parentHref: string
  /**
   * Crumbs between the collection and the current page, for a page nested
   * under an entity (Domains / mail.acme.com / Unsubscribes). Pass a stable
   * (memoized) array: a new one each render re-publishes the title.
   */
  trail?: ReadonlyArray<{ label: string; href: string }>
  /**
   * Current entity label. Null while it loads: the header holds the parent's
   * name, as the parent page reads, and hides `actions`. A loading shell can
   * mount this with `title={null}` so the header never goes blank. A page
   * with a `trail` knows its own title up front, so it never passes null.
   */
  title: string | null
  /**
   * Replaces the static current-page crumb, for a title edited in place (a
   * group's click-to-rename name). It renders its own `aria-current="page"`
   * element in `PAGE_TITLE_CLASSES`. Pass a stable (memoized) element, like
   * `trail`.
   */
  titleSlot?: ReactNode
  /** Optional entity-level action rendered before the global header actions. */
  actions?: ReactNode
}

/**
 * Shared app-header foundation for every page nested under a section
 * (collection → entity, or section → sub-page): `Parent / … / Child`, the
 * parent a plain link back. It owns the breadcrumb structure, canonical title
 * styling, and PageTitleContext lifecycle so nested pages never duplicate
 * navigation chrome (a back arrow, an in-body breadcrumb) inside their body.
 */
export function DetailPageHeader({
  parentLabel,
  parentHref,
  trail,
  title,
  titleSlot,
  actions = null,
}: DetailPageHeaderProps) {
  const setPageTitle = useSetPageTitle()

  const titleContent = useMemo(() => {
    if (title === null) {
      return <h1 className={PAGE_TITLE_CLASSES}>{parentLabel}</h1>
    }

    return (
      <Breadcrumb className="min-w-0">
        <BreadcrumbList className="min-w-0 flex-nowrap gap-1.5">
          <BreadcrumbItem className="shrink-0">
            <BreadcrumbLink href={parentHref} className="font-medium">
              {parentLabel}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          {trail?.map((crumb) => (
            <Fragment key={crumb.href}>
              <BreadcrumbItem className="min-w-0">
                <BreadcrumbLink href={crumb.href} className="truncate">
                  {crumb.label}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </Fragment>
          ))}
          <BreadcrumbItem className="min-w-0">
            {titleSlot ?? (
              <BreadcrumbPage className={cn(PAGE_TITLE_CLASSES, 'min-w-0')}>
                {title}
              </BreadcrumbPage>
            )}
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }, [parentHref, parentLabel, title, titleSlot, trail])

  useEffect(() => {
    setPageTitle({ titleContent, rightContent: title ? actions : null })
    return () => setPageTitle({ titleContent: null, rightContent: null })
  }, [actions, setPageTitle, title, titleContent])

  return null
}
